<template>
  <ControlWrapper
    v-bind="control.controlWrapper.value"
    :styles="control.styles"
    :is-focused="control.isFocused.value"
    :applied-options="control.appliedOptions.value"
  >
    <SectionWrapper id="license-plate-confirm" class="flex flex-col">
      <template #header>
        <div class="flex w-full items-center justify-between" :class="control.styles.control.label">
          <h3>
            {{ control.computedLabel.value }}
          </h3>
        </div>
      </template>
      <WStepperInput
        v-model="newNumberOfPassengers"
        type="number"
        :aria-roledescription="inputLabel"
        :label="inputLabel"
        @update:model-value="setNumberOfPassengers"
      />
    </SectionWrapper>
  </ControlWrapper>
</template>

<script setup lang="ts">
  import { ControlElement } from '@jsonforms/core';
  import { computed, nextTick, ref } from 'vue';
  import { rendererProps, useJsonFormsControl } from '@jsonforms/vue';
  import { useWidgetControl } from '../util';
  import SectionWrapper from 'components/claims-widget/components/SectionWrapper.vue';
  import ControlWrapper from './ControlWrapper.vue';
  import WStepperInput from '../../base/StepperInput.vue';
  import { useDebounceFn } from '@vueuse/core';

  interface NumberOfPassengersOptions {
    label?: string;
    max?: number;
    min?: number;
  }

  const props = defineProps(rendererProps<ControlElement>());
  const control = useWidgetControl(useJsonFormsControl(props));

  const numberOfPassengersOptions = ref(
    (control.control.value.schema as unknown as { passengersSection: NumberOfPassengersOptions }).passengersSection
  );
  const numberOfPassengersError = ref('');
  const inputLabel = computed(() => numberOfPassengersOptions.value.label);

  const newNumberOfPassengers = ref<number>(control.control.value.data || 0);

  const submitNumberOfPassengers = useDebounceFn(() => {
    control.onChange(newNumberOfPassengers.value || 0);
  }, 500);

  submitNumberOfPassengers();

  function setNumberOfPassengers(numberOfPassengers: number | string) {
    if (numberOfPassengers === '') {
      numberOfPassengers = 0;
      newNumberOfPassengers.value = 0;
    }
    nextTick(() => {
      const rgx = new RegExp('[^0-9]', 'g');
      newNumberOfPassengers.value = Number(String(newNumberOfPassengers.value).replaceAll(rgx, ''));
      const min = numberOfPassengersOptions.value.min || 0;
      const max = numberOfPassengersOptions.value.max || 50;
      newNumberOfPassengers.value = Number(numberOfPassengers);
      if (newNumberOfPassengers.value < min) {
        newNumberOfPassengers.value = min;
      }
      if (newNumberOfPassengers.value > max) {
        newNumberOfPassengers.value = max;
      }
      if (newNumberOfPassengers.value >= min && newNumberOfPassengers.value <= max) {
        numberOfPassengersError.value = '';
        submitNumberOfPassengers();
        return;
      }

      numberOfPassengersError.value = 'Must be a valid passenger count';
    });
  }
</script>
